/**
 * «Cambió el plano» para DWG/DXF (OPUS ci, idea 4, tramo T4a): la parte PURA que arma lo comparable de un dibujo a
 * partir de su `inventario` (lo que el servidor ya guarda; no se toca el sidecar).
 *
 * El inventario de un DWG NO trae una lista por entidad: trae agregados por capa, por tipo de bloque y por vista
 * (planta). Por eso la comparación es por AGREGADO:
 *  - una pieza por (capa, planta que suma) con su largo, su área y sus vanos; emparejada por NOMBRE de capa y por la
 *    planta que `emparejarVistas` dice que es la misma (el id 'v2' es posicional: puede ser otra planta en la
 *    revisión nueva);
 *  - una pieza por handle de cada bloque (los INSERT sí traen handle), emparejada por ese handle SIEMPRE como DUDOSA:
 *    sin centro no se puede comprobar que sea la misma inserción. Un renglón que cubre el bloque entero no lo nota (toma todos
 *    los handles de la revisión); uno PARCIAL (por handle) queda «revisar».
 * La cantidad de cada renglón NO sale de aquí: se vuelve a medir la misma selección con la medición de la pantalla
 * sobre el inventario de la revisión nueva (`medirElemento`), igual que en IFC.
 *
 * También vive aquí lo que hace comparables las dos mediciones: la escala (si la unidad no es la misma, se frena) y
 * la altura de cada planta de la revisión nueva SIN gastar IA.
 */
import type { PlanoInventario, PlanoEscalaElegida, PlanoVista } from './domain.js';
import { factorDeEscala } from './escalaDelPlano.js';
import { medidaDeCapaDwg } from './medidaDeCapaDwg.js';
import type { PiezaComparable, ResultadoVistas, VistaComparable } from './cambioDePlano.js';

// ---------------------------------------------------------------------------------------------------
// Vistas
// ---------------------------------------------------------------------------------------------------

/** Las vistas de un inventario, listas para `emparejarVistas`. */
export function vistasComparables(inv: PlanoInventario | null | undefined): VistaComparable[] {
  return (inv?.vistas ?? []).map((v) => ({
    id: v.id,
    rotulo: v.rotulo,
    tipo: v.tipo,
    clase: v.clase ?? null,
    ancho_m: v.ancho_m,
    alto_m: v.alto_m,
  }));
}

/** Rótulo de una vista para mostrar, en tipo oración («PLANTA BAJA» → «Planta baja»). Sin rótulo: «Vista 1». */
export function rotuloDeLaVista(v: Pick<PlanoVista, 'id' | 'rotulo'> | undefined, i: number): string {
  const r = v?.rotulo?.trim();
  if (!r) return `Vista ${i + 1}`;
  const t = r.toLocaleLowerCase('es-MX');
  return t.charAt(0).toLocaleUpperCase('es-MX') + t.slice(1);
}

// ---------------------------------------------------------------------------------------------------
// Piezas
// ---------------------------------------------------------------------------------------------------

export interface OpcionesPiezasDwg {
  /** 'a' = el dibujo con el que se armó la hoja; 'b' = la revisión nueva. */
  lado: 'a' | 'b';
  /** Solo en 'b': cómo se emparejaron las plantas (`emparejarVistas(vistasA, vistasB)`). */
  vistas?: ResultadoVistas;
  /** ¿La capa se mide como muro? (la clasificación de capas y lo que decidió la persona). */
  esMuro: (capa: string) => boolean;
}

const r2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Las piezas comparables de un dibujo. `factor` = unidad del dibujo → metro vigente (null = sin escala: las capas no
 * traen medidas y solo se comparan por su existencia).
 *
 * Capas: una pieza por planta que SUMA (lo de cortes y fachadas no entra a la hoja); si la capa no trae `por_vista`,
 * una sola. `identidadPorNombre` = la planta se emparejó por su rótulo (pareja segura); por forma u orden, la pareja
 * sale dudosa y nada cambia sola.
 * Bloques: una pieza por handle; la identidad es el handle (solo vale con el mismo bloque).
 */
export function piezasDwg(inv: PlanoInventario | null | undefined, factor: number | null, o: OpcionesPiezasDwg): PiezaComparable[] {
  if (!inv) return [];
  const out: PiezaComparable[] = [];
  const vistas = inv.vistas ?? [];
  const vistaPorId = new Map(vistas.map((v, i) => [v.id, { v, i }] as const));
  const rotulo = (id: string) => {
    const x = vistaPorId.get(id);
    return rotuloDeLaVista(x?.v, x?.i ?? 0);
  };

  // El lector solo emite `por_vista` cuando el dibujo tiene MÁS DE UNA vista. Con una sola planta que suma, la capa sin
  // `por_vista` es de esa planta: se emite como su pieza (si no, al agregar o quitar una planta entre revisiones la capa
  // no se empareja nunca y su renglón se pone en 0).
  const sumando = vistas.filter((v) => v.suma);
  const unicaVista = sumando.length === 1 ? sumando[0]! : null;

  for (const capa of inv.capas ?? []) {
    const esMuro = o.esMuro(capa.capa);
    const todasLasVistas = capa.por_vista ?? [];
    const porVista = todasLasVistas.filter((pv) => vistaPorId.get(pv.vista)?.v.suma !== false);
    // Capa que solo está en vistas que NO suman (cortes, fachadas): no entra a la hoja, no se compara.
    if (todasLasVistas.length > 0 && porVista.length === 0) continue;
    if (porVista.length === 0 && unicaVista) {
      const m = medidaDeCapaDwg(capa, factor, esMuro);
      const par = o.lado === 'b' ? o.vistas?.parejas.find((x) => x.b === unicaVista.id) : undefined;
      out.push({
        id: `${capa.capa}@${unicaVista.id}`,
        gid: o.lado === 'a' ? `${capa.capa}@${unicaVista.id}` : par ? `${capa.capa}@${par.a}` : `${capa.capa}@B:${unicaVista.id}`,
        grupo: capa.capa,
        clase: 'capa',
        nivel: unicaVista.id,
        nombre: `${capa.capa} · ${rotulo(unicaVista.id)}`,
        identidadPorNombre: o.lado === 'a' ? true : !!par && !par.dudoso,
        medidas: {
          ...(m.baseLargo != null ? { longitud: r2(m.baseLargo) } : {}),
          ...(m.areaM2 != null ? { area: r2(m.areaM2) } : {}),
          vanos: capa.vanos?.length ?? 0,
        },
      });
      continue;
    }
    if (porVista.length > 0) {
      for (const pv of porVista) {
        const m = medidaDeCapaDwg(pv, factor, esMuro);
        const par = o.lado === 'b' ? o.vistas?.parejas.find((x) => x.b === pv.vista) : undefined;
        out.push({
          id: `${capa.capa}@${pv.vista}`,
          // La identidad de una capa en una planta es «capa@planta de A»; en B, la planta de A con la que se emparejó.
          gid: o.lado === 'a' ? `${capa.capa}@${pv.vista}` : par ? `${capa.capa}@${par.a}` : `${capa.capa}@B:${pv.vista}`,
          grupo: capa.capa,
          clase: 'capa',
          nivel: pv.vista,
          nombre: `${capa.capa} · ${rotulo(pv.vista)}`,
          identidadPorNombre: o.lado === 'a' ? true : !!par && !par.dudoso,
          medidas: {
            ...(m.baseLargo != null ? { longitud: r2(m.baseLargo) } : {}),
            ...(m.areaM2 != null ? { area: r2(m.areaM2) } : {}),
            vanos: pv.vanos?.length ?? 0,
          },
        });
      }
    } else {
      const m = medidaDeCapaDwg(capa, factor, esMuro);
      out.push({
        id: capa.capa,
        gid: capa.capa,
        grupo: capa.capa,
        clase: 'capa',
        nivel: null,
        nombre: capa.capa,
        identidadPorNombre: true,
        medidas: {
          ...(m.baseLargo != null ? { longitud: r2(m.baseLargo) } : {}),
          ...(m.areaM2 != null ? { area: r2(m.areaM2) } : {}),
          vanos: capa.vanos?.length ?? 0,
        },
      });
    }
  }

  for (const b of inv.bloques ?? []) {
    for (const handle of b.ids ?? []) {
      out.push({ id: handle, gid: handle, grupo: b.nombre, clase: 'bloque', nivel: null, nombre: b.nombre, centro: null, medidas: {} });
    }
  }
  return out;
}

// ---------------------------------------------------------------------------------------------------
// Escala
// ---------------------------------------------------------------------------------------------------

export type EscalaComparable = { ok: true; factor: number; aviso?: string } | { ok: false; motivo: string };

const IGUALES = 1e-9;
const nombreUnidad = (inv: PlanoInventario | null | undefined, e: PlanoEscalaElegida | null | undefined): string =>
  e?.unidad ?? inv?.escala?.unidad ?? inv?.escala?.header.unidad ?? 'otra unidad';

/**
 * ¿Se pueden medir las dos revisiones con la MISMA unidad? Una revisión en otra unidad (o con una escala que no se
 * sabe, o imposible) mediría todo distinto: la comparación se frena y se dice por qué; nunca se mide la revisión nueva
 * con un factor distinto del de la hoja.
 *  - `elegidaA` = la unidad que la persona eligió para el archivo de la hoja (si la hubo).
 *  - `elegidaB` = la del archivo de la revisión (al crearla se copia la de A, así que normalmente es la misma).
 * Si solo el header (`$INSUNITS`) declara otra unidad, se avisa y se sigue.
 * OJO: la pantalla del plano puede fijar una unidad a mano (`unidadPlanoSel`), pero hoy no tiene forma de cambiarla y no
 * se guarda ni en la hoja ni en la foto del renglón: aquí se compara con la unidad del ARCHIVO (la elegida, si la hay).
 */
export function escalaComparable(
  invA: PlanoInventario | null | undefined,
  elegidaA: PlanoEscalaElegida | null | undefined,
  invB: PlanoInventario | null | undefined,
  elegidaB: PlanoEscalaElegida | null | undefined,
): EscalaComparable {
  const fA = factorDeEscala(invA, elegidaA ?? null).factor;
  const fB = factorDeEscala(invB, elegidaB ?? null).factor;
  if (fA == null) return { ok: false, motivo: 'Todavía no sé en qué unidad está dibujado el plano de tu hoja: elígela en el plano y vuelve a comparar.' };
  if (fB == null) {
    return { ok: false, motivo: 'No sé en qué unidad está dibujada la revisión nueva, así que no puedo medirla igual que tu hoja. Descártala y súbela de nuevo.' };
  }
  if (Math.abs(fA - fB) > IGUALES) {
    return {
      ok: false,
      motivo: `La revisión nueva está dibujada en otra unidad (${nombreUnidad(invB, elegidaB)}) que el plano de tu hoja (${nombreUnidad(invA, elegidaA)}): mediría todo distinto. Revisa el archivo.`,
    };
  }
  const candidata = invB?.escala?.candidatas?.find((c) => Math.abs(c.factor - fB) < IGUALES);
  if (candidata && candidata.imposibles > 0) {
    return { ok: false, motivo: 'Con esa unidad la revisión nueva trae medidas que no pueden ser de una obra (muros o puertas imposibles): revisa el archivo.' };
  }
  const hA = invA?.escala?.header.unidad ?? null;
  const hB = invB?.escala?.header.unidad ?? null;
  if (hA !== hB) {
    return { ok: true, factor: fA, aviso: 'El archivo de la revisión declara otra unidad que el anterior; se midió con la misma que tu hoja.' };
  }
  return { ok: true, factor: fA };
}

// ---------------------------------------------------------------------------------------------------
// Alturas de planta
// ---------------------------------------------------------------------------------------------------

/** La altura de entrepiso de una planta y de dónde sale (la misma forma que `alturaPorVista` de la pantalla). */
export interface AlturaDeVista {
  altoM: number | null;
  origen: string;
}

const SIN_ALTURA: AlturaDeVista = { altoM: null, origen: 'el plano nuevo no dice la altura de esta planta: escríbela' };
/** 5 mm: la cota y la altura elegida por la IA no se redondean igual. */
const MISMA_ALTURA = 0.005;

/**
 * La altura de cada planta que SUMA de la revisión nueva, sin gastar IA (no se llama a la propuesta de altura):
 *  1. la que traen sus propias cotas de elevación;
 *  2. si no, la de su planta pareja de A —solo si el emparejado fue por rótulo (seguro) y la revisión trae esa misma
 *     altura entre sus entrepisos candidatos (`candidatas_m`)—;
 *  3. si no, sin dato: el renglón que la necesite queda «revisar».
 * Nunca se copia una altura de A que la revisión no respalde: una planta con otra altura da «cambia» y lo dice.
 */
export function alturasDeB(
  alturasA: ReadonlyMap<string, AlturaDeVista>,
  invB: PlanoInventario | null | undefined,
  vistas: ResultadoVistas,
): Map<string, AlturaDeVista> {
  const out = new Map<string, AlturaDeVista>();
  for (const v of invB?.vistas ?? []) {
    if (!v.suma) continue;
    const al = v.altura;
    if (al?.origen === 'cota_de_elevacion' && al.altura_m != null) {
      out.set(v.id, { altoM: al.altura_m, origen: 'de las cotas de la revisión nueva' });
      continue;
    }
    const par = vistas.parejas.find((x) => x.b === v.id && !x.dudoso);
    const alto = par ? alturasA.get(par.a)?.altoM ?? null : null;
    if (alto != null && (al?.candidatas_m ?? []).some((c) => Math.abs(c - alto) < MISMA_ALTURA)) {
      out.set(v.id, { altoM: alto, origen: 'la misma que tiene tu hoja para esta planta (la revisión nueva trae esa altura)' });
      continue;
    }
    out.set(v.id, SIN_ALTURA);
  }
  return out;
}

// ---------------------------------------------------------------------------------------------------
// Cambios de planta (solo para la pantalla; no van en `ComparacionDePlanos`, que es lo que el servidor valida)
// ---------------------------------------------------------------------------------------------------

export interface CambioDeVista {
  tipo: 'agregada' | 'quitada' | 'altura' | 'dudosa';
  rotulo: string;
  detalle: string;
}

const f2 = (n: number) => n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Qué cambió en las plantas: una planta agregada o quitada, una pareja que no es segura (se emparejó por su forma o su
 * orden) y una planta cuya altura de entrepiso es otra.
 */
export function cambiosDeVistas(
  invA: PlanoInventario | null | undefined,
  invB: PlanoInventario | null | undefined,
  vistas: ResultadoVistas,
  alturasA: ReadonlyMap<string, AlturaDeVista>,
  alturasB: ReadonlyMap<string, AlturaDeVista>,
): CambioDeVista[] {
  const out: CambioDeVista[] = [];
  const vA = new Map((invA?.vistas ?? []).map((v, i) => [v.id, { v, i }] as const));
  const vB = new Map((invB?.vistas ?? []).map((v, i) => [v.id, { v, i }] as const));
  const nombre = (m: Map<string, { v: PlanoVista; i: number }>, id: string) => {
    const x = m.get(id);
    return rotuloDeLaVista(x?.v, x?.i ?? 0);
  };
  for (const id of vistas.soloA) {
    if (vA.get(id)?.v.suma) out.push({ tipo: 'quitada', rotulo: nombre(vA, id), detalle: 'Esta planta ya no está en la revisión nueva.' });
  }
  for (const id of vistas.soloB) {
    if (vB.get(id)?.v.suma) out.push({ tipo: 'agregada', rotulo: nombre(vB, id), detalle: 'Es una planta nueva en la revisión.' });
  }
  for (const par of vistas.parejas) {
    const rot = nombre(vB, par.b);
    if (par.dudoso) {
      out.push({ tipo: 'dudosa', rotulo: rot, detalle: 'No es seguro que sea la misma planta que antes: las emparejé por su forma, no por su nombre. Revisa sus renglones.' });
    }
    const a = alturasA.get(par.a)?.altoM ?? null;
    const b = alturasB.get(par.b)?.altoM ?? null;
    if (a != null && b != null && Math.abs(a - b) > MISMA_ALTURA) {
      out.push({ tipo: 'altura', rotulo: rot, detalle: `La altura de esta planta pasó de ${f2(a)} a ${f2(b)} m.` });
    }
  }
  return out;
}
